import {Box, Card, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import React, {useCallback, useEffect, useState} from "react";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import {ReactSortable} from "react-sortablejs";
import Grid2 from "@mui/material/Unstable_Grid2";
import Utils from "../../../../core/services/utils/utils";


const RearrangeEntriesDialog = ({open, close, title, entries, onRearrange}) => {
    const [list, setList] = useState([]);

    /**
     * With each change in the list, update the state
     */
    useEffect(() => {
        if (!open) {
            return setList([]);
        }
        setList(entries?.map((e, i) => ({...e, id: e.key, orderIndex: i})) ?? [])
    }, [entries, open]);

    /**
     * Calls the provided rearranging callback
     * @type {(function(): void)|*}
     */
    const saveChanges = useCallback(() => {
        const rearrangedKeys = list.map(entry => entry.key);
        const res = [];
        for (const entry of entries) {
            res[rearrangedKeys.indexOf(entry.key)] = entry;
        }
        onRearrange(res);
        close();
    }, [close, entries, list, onRearrange]);

    /**
     * Updates the list entries based on the drag and drop event
     * @type {(function(*): void)|*}
     */
    const updateListEntries = useCallback((e) => {
        setList(prevState => Utils.reorderList(prevState, e.oldDraggableIndex, e.newDraggableIndex, 'orderIndex'));
    }, [])

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'sm'}
                className={'bound-dialog rearrange-entries-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'} component={'div'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {title ?? "Rearrange Entries"}
                    </p>
                </DialogTitle>
                <DialogContent
                    className={'p-3 d-flex flex-column align-items-center w-100'}
                    dividers
                >
                    <Grid2
                        container
                        spacing={1}
                        component={ReactSortable}
                        sort
                        className={'entries-container'}
                        list={list}
                        setList={() => void 0}
                        onUpdate={updateListEntries}
                        handle={'.handle'}
                        group={'entries'}
                        animation={210}
                        forceFallback={false}
                        ghostClass={"dragged-entry"}
                    >
                        {list.map((entry) => (
                            <Grid2 key={entry.key} xs={entry.size}>
                                <Card
                                    variant={'outlined'}
                                    className={'handle'}
                                    sx={(theme) => ({
                                        padding: 3,
                                        backgroundColor: `rgba(${theme.palette.primary.mainChannel} / ${theme.palette.action.hoverOpacity})`,
                                        borderRadius: 1,
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    })}
                                >
                                    <Typography variant="caption" fontWeight={600} color={'primary.main'} textAlign={'center'}>
                                        {entry.title ?? ""}
                                    </Typography>
                                </Card>
                            </Grid2>
                        ))}
                    </Grid2>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => close()}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Back
                        </Button>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            borderRadius={'8px 0 8px 0'}
                            onClick={() => saveChanges()}
                        >
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default RearrangeEntriesDialog;